import React, { useEffect, useState, useCallback } from 'react';
import AppLayout from '../components/AppLayout';
import {
  listModelPortfolios,
  getModelPortfolioPositions,
  getModelPortfolioEvents,
} from '../api/modelPortfolioService';
import './ModelPortfolio.css';

const toNum = (v) => {
  if (v === null || v === undefined) return null;
  const n = parseFloat(v.toString());
  return Number.isNaN(n) ? null : n;
};

const fmt = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 });

const fmtMoney = (n) =>
  n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : `₹${fmt(n)}`;

const fmtSignedMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const num = Number(n);
  const sign = num > 0 ? '+' : num < 0 ? '−' : '';
  return `${sign}₹${fmt(Math.abs(num))}`;
};

const fmtSignedPct = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '—';
  const sign = Number(n) > 0 ? '+' : Number(n) < 0 ? '−' : '';
  return `${sign}${fmt(Math.abs(n))}%`;
};

const pnlClass = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '';
  if (Number(n) > 0) return 'mp-pnl-pos';
  if (Number(n) < 0) return 'mp-pnl-neg';
  return '';
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Holding period elapsed since the portfolio was created by admin.
const holdingSince = (iso) => {
  if (!iso) return '—';
  const start = new Date(iso);
  if (Number.isNaN(start.getTime())) return '—';
  const now = new Date();
  let months =
    (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 1) {
    const days = Math.max(0, Math.floor((now - start) / 86400000));
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (months < 12) return `${months} mo`;
  const years = Math.floor(months / 12);
  const rem = months % 12;
  return rem ? `${years}y ${rem}m` : `${years}y`;
};

const riskClass = (level) => {
  const l = (level || '').toLowerCase();
  if (l === 'low') return 'mp-badge-low';
  if (l === 'high') return 'mp-badge-high';
  return 'mp-badge-moderate';
};

const RiskBadge = ({ level }) => (
  <span className={`mp-badge ${riskClass(level)}`}>{level || 'MODERATE'}</span>
);

// Admin's call on the holding: HOLD / EXIT / NEW
const HoldingChip = ({ status }) => {
  const s = (status || 'HOLD').toUpperCase();
  const cls =
    s === 'EXIT' ? 'mp-chip-exit' : s === 'NEW' ? 'mp-chip-new' : 'mp-chip-hold';
  return <span className={`mp-chip ${cls}`}>{s}</span>;
};

const StatusChip = ({ action, maxBuyPrice }) => {
  if (action === 'BUY_ALLOWED')
    return <span className="mp-chip mp-chip-buy">Within Buy Range</span>;
  if (action === 'NO_NEW_ENTRY')
    return (
      <span className="mp-chip mp-chip-no">
        No Entry &gt; {fmtMoney(maxBuyPrice)}
      </span>
    );
  return <span className="mp-chip mp-chip-na">No Price</span>;
};

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const FIELD_LABELS = {
  weightPercent: 'Weight %',
  maxBuyPrice: 'Max Buy',
  recordPrice: 'Record Price',
  status: 'Status',
  name: 'Name',
  description: 'Description',
  riskLevel: 'Risk Level',
  themeCategory: 'Theme',
  strategyType: 'Strategy',
  holdingPeriodMonths: 'Holding Period',
};

const fmtChangeValue = (field, v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (field === 'maxBuyPrice' || field === 'recordPrice') return fmtMoney(v);
  if (field === 'weightPercent') return `${fmt(v)}%`;
  if (field === 'holdingPeriodMonths') return `${v}mo`;
  return String(v);
};

// Renders one event card inside a rebalance batch.
const EventRow = ({ ev }) => {
  if (ev.kind === 'STOCK_ADDED') {
    const a = ev.after || {};
    return (
      <div className="mp-ev-row mp-ev-add">
        <span className="mp-ev-tag">+ ADDED</span>
        <span className="mp-ev-sym">{ev.symbol}</span>
        <span className="mp-ev-meta">
          {a.weightPercent ? `${fmt(a.weightPercent)}%` : ''}
          {a.maxBuyPrice ? ` · Max ${fmtMoney(a.maxBuyPrice)}` : ''}
          {a.recordPrice ? ` · Rec ${fmtMoney(a.recordPrice)}` : ''}
        </span>
      </div>
    );
  }
  if (ev.kind === 'STOCK_REMOVED') {
    return (
      <div className="mp-ev-row mp-ev-rem">
        <span className="mp-ev-tag">− REMOVED</span>
        <span className="mp-ev-sym">{ev.symbol}</span>
      </div>
    );
  }
  if (ev.kind === 'STOCK_EXITED') {
    return (
      <div className="mp-ev-row mp-ev-exit">
        <span className="mp-ev-tag">EXIT CALL</span>
        <span className="mp-ev-sym">{ev.symbol}</span>
        <span className="mp-ev-meta">Admin marked EXIT — book out</span>
      </div>
    );
  }
  if (ev.kind === 'STOCK_CHANGED') {
    const changes = ev.summary || {};
    return (
      <div className="mp-ev-row mp-ev-chg">
        <span className="mp-ev-tag">CHANGED</span>
        <span className="mp-ev-sym">{ev.symbol}</span>
        <div className="mp-ev-diff">
          {Object.entries(changes).map(([field, { before, after }]) => (
            <span key={field} className="mp-ev-diff-item">
              <em>{FIELD_LABELS[field] || field}</em>{' '}
              {fmtChangeValue(field, before)} → {fmtChangeValue(field, after)}
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (ev.kind === 'META_UPDATED') {
    const changes = ev.summary || {};
    return (
      <div className="mp-ev-row mp-ev-meta-row">
        <span className="mp-ev-tag">META</span>
        <div className="mp-ev-diff">
          {Object.entries(changes).map(([field, { before, after }]) => (
            <span key={field} className="mp-ev-diff-item">
              <em>{FIELD_LABELS[field] || field}</em>{' '}
              {fmtChangeValue(field, before)} → {fmtChangeValue(field, after)}
            </span>
          ))}
        </div>
      </div>
    );
  }
  if (ev.kind === 'PORTFOLIO_CREATED') {
    return (
      <div className="mp-ev-row mp-ev-add">
        <span className="mp-ev-tag">LAUNCHED</span>
        <span className="mp-ev-meta">
          Portfolio launched with {ev.summary?.stocks ?? 0} stocks
        </span>
      </div>
    );
  }
  if (ev.kind === 'PORTFOLIO_ARCHIVED') {
    return (
      <div className="mp-ev-row mp-ev-rem">
        <span className="mp-ev-tag">ARCHIVED</span>
        <span className="mp-ev-meta">Portfolio archived by admin</span>
      </div>
    );
  }
  if (ev.kind === 'PORTFOLIO_REACTIVATED') {
    return (
      <div className="mp-ev-row mp-ev-add">
        <span className="mp-ev-tag">REACTIVATED</span>
        <span className="mp-ev-meta">Portfolio reactivated</span>
      </div>
    );
  }
  return null;
};

const RebalanceBatch = ({ batch }) => {
  const [open, setOpen] = useState(false);
  const s = batch.summary || {};
  const isRebalance = batch.kinds.includes('REBALANCE');
  const perStockEvents = batch.events.filter((e) => e.kind !== 'REBALANCE');

  return (
    <div className="mp-batch">
      <button
        type="button"
        className="mp-batch-head"
        onClick={() => setOpen((o) => !o)}
      >
        <div className="mp-batch-head-left">
          <span className="mp-batch-date">{fmtDateTime(batch.createdAt)}</span>
          {isRebalance ? (
            <span className="mp-batch-pill">
              {s.added ? `+${s.added} added` : ''}
              {s.added && (s.removed || s.exited || s.changed) ? ' · ' : ''}
              {s.removed ? `${s.removed} removed` : ''}
              {s.removed && (s.exited || s.changed) ? ' · ' : ''}
              {s.exited ? `${s.exited} exited` : ''}
              {s.exited && s.changed ? ' · ' : ''}
              {s.changed ? `${s.changed} changed` : ''}
            </span>
          ) : (
            <span className="mp-batch-pill">
              {batch.kinds.join(', ')}
            </span>
          )}
        </div>
        <span className="mp-batch-caret">{open ? '▾' : '▸'}</span>
      </button>
      {batch.note && <div className="mp-batch-note">“{batch.note}”</div>}
      {open && (
        <div className="mp-batch-body">
          {perStockEvents.map((ev) => (
            <EventRow key={ev._id} ev={ev} />
          ))}
        </div>
      )}
    </div>
  );
};

const ModelPortfolio = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [capital, setCapital] = useState(100000);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null); // sized positions
  const [showInfo, setShowInfo] = useState(false); // legend/info modal
  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [showPriceOnly, setShowPriceOnly] = useState(false);

  const loadPortfolios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listModelPortfolios();
      setPortfolios(res.data?.data || []);
    } catch (e) {
      setError('Failed to load portfolios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPortfolios();
  }, [loadPortfolios]);

  const runSizing = useCallback(async (portfolioId, cap) => {
    if (!portfolioId || !cap || cap <= 0) return;
    setCalculating(true);
    try {
      const res = await getModelPortfolioPositions(portfolioId, cap);
      setResult(res.data?.data || null);
    } catch (e) {
      setResult(null);
      alert('Failed to calculate allocation: ' + (e.response?.data?.message || e.message));
    } finally {
      setCalculating(false);
    }
  }, []);

  const loadEvents = useCallback(async (portfolioId, includePriceOnly) => {
    if (!portfolioId) return;
    setEventsLoading(true);
    try {
      const res = await getModelPortfolioEvents(portfolioId, {
        limit: 50,
        includePriceOnly,
      });
      setEvents(res.data?.data || []);
    } catch (e) {
      setEvents([]);
    } finally {
      setEventsLoading(false);
    }
  }, []);

  const selectPortfolio = (p) => {
    setSelected(p);
    setResult(null);
    setEvents([]);
    // Auto-run sizing with the current capital so P&L + allocation show immediately
    const cap = parseFloat(capital);
    if (cap > 0) runSizing(p._id, cap);
    loadEvents(p._id, showPriceOnly);
  };

  const calculate = useCallback(() => {
    if (!selected) return;
    const cap = parseFloat(capital);
    if (!cap || cap <= 0) {
      alert('Please enter a valid capital amount.');
      return;
    }
    runSizing(selected._id, cap);
  }, [selected, capital, runSizing]);

  const togglePriceOnly = () => {
    const next = !showPriceOnly;
    setShowPriceOnly(next);
    if (selected) loadEvents(selected._id, next);
  };

  return (
    <AppLayout pageBadge="Model Portfolios">
      <div className="mp-root">
        {/* ── Left: list ─────────────────────────────────── */}
        <section className="mp-panel">
          <div className="mp-panel-head">
            <span>Available Portfolios</span>
            <span className="mp-badge mp-badge-soft">{portfolios.length}</span>
          </div>
          <div className="mp-panel-body">
            {loading && (
              <div className="mp-empty">
                <div className="icon">⏳</div>
                <div>Loading…</div>
              </div>
            )}
            {!loading && error && (
              <div className="mp-empty">
                <div className="icon">⚠️</div>
                <div>{error}</div>
              </div>
            )}
            {!loading && !error && portfolios.length === 0 && (
              <div className="mp-empty">
                <div className="icon">📭</div>
                <div>No portfolios available yet.</div>
              </div>
            )}
            {!loading &&
              !error &&
              portfolios.map((p) => (
                <div
                  key={p._id}
                  className={`mp-card${selected?._id === p._id ? ' active' : ''}`}
                  onClick={() => selectPortfolio(p)}
                >
                  <h3>{p.name}</h3>
                  <div className="mp-meta">
                    <RiskBadge level={p.riskLevel} />
                    {p.themeCategory && (
                      <span className="mp-badge mp-badge-soft">{p.themeCategory}</span>
                    )}
                    {p.strategyType && (
                      <span className="mp-badge mp-badge-soft">{p.strategyType}</span>
                    )}
                    <span className="mp-badge mp-badge-soft">
                      {(p.stocks || []).length} stocks
                    </span>
                    {p.holdingPeriodMonths ? (
                      <span className="mp-badge mp-badge-soft">
                        {p.holdingPeriodMonths}M hold
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
        </section>

        {/* ── Right: detail + sizing ─────────────────────── */}
        <section className="mp-panel">
          <div className="mp-panel-head">
            <span>{selected ? selected.name : 'Select a portfolio'}</span>
            {selected && (
              <button
                type="button"
                className="mp-info-btn mp-portfolio-info-btn"
                aria-label={`Details for ${selected.name}`}
                onClick={() => setShowInfo(true)}
              >
                i
                {selected.description && (
                  <span className="mp-panel-description" role="tooltip">
                    {selected.description}
                  </span>
                )}
              </button>
            )}
          </div>
          <div className="mp-panel-body mp-detail-body">
            {!selected ? (
              <div className="mp-empty">
                <div className="icon">👈</div>
                <div>
                  Pick a portfolio from the left to view its holdings
                  <br />
                  and calculate your allocation.
                </div>
              </div>
            ) : (
              <>
                {/* ── Compact info strip ───────────────────────── */}
                <div className="mp-strip">
                  <div className="mp-strip-item">
                    <span className="k">Created</span>
                    <span className="v">{fmtDate(selected.createdAt)}</span>
                  </div>
                  <div className="mp-strip-item">
                    <span className="k">Holding</span>
                    <span className="v">{holdingSince(selected.createdAt)}</span>
                  </div>
                  <div className="mp-strip-item">
                    <span className="k">Risk</span>
                    <span className="v"><RiskBadge level={selected.riskLevel} /></span>
                  </div>
                  {selected.themeCategory && (
                    <div className="mp-strip-item">
                      <span className="k">Theme</span>
                      <span className="v">{selected.themeCategory}</span>
                    </div>
                  )}
                  {result && (
                    <>
                      <div className="mp-strip-item">
                        <span className="k">Capital</span>
                        <span className="v">{fmtMoney(result.capital)}</span>
                      </div>
                      <div className="mp-strip-item">
                        <span className="k">Allocated</span>
                        <span className="v">{fmtMoney(result.totalAllocated)}</span>
                      </div>
                    </>
                  )}
                </div>

                {/* ── Model performance since record (weight-based) ───────────── */}
                {result && (
                  <div className={`mp-pnl-card ${pnlClass(result.totalPnl)}`}>
                    <div className="mp-pnl-main">
                      <span className="mp-pnl-label">Model P&amp;L (since record)</span>
                      <span className="mp-pnl-value">
                        {fmtSignedMoney(result.totalPnl)}
                        <span className="mp-pnl-pct">{fmtSignedPct(result.totalPnlPercent)}</span>
                      </span>
                    </div>
                    <div className="mp-pnl-sub">
                      <div>
                        <span className="lbl">Model Basis</span>
                        <span className="val">{fmtMoney(result.totalInvestedAtRecord)}</span>
                      </div>
                      <div>
                        <span className="lbl">Model Value</span>
                        <span className="val">{fmtMoney(result.totalCurrentValue)}</span>
                      </div>
                      <div>
                        <span className="lbl">Your Allocation</span>
                        <span className="val">{fmtMoney(result.totalAllocated)}</span>
                      </div>
                      <div>
                        <span className="lbl">Unallocated</span>
                        <span className="val">{fmtMoney(result.unallocated)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="mp-capital-bar">
                  <input
                    type="number"
                    min="0"
                    step="1000"
                    placeholder="Enter your capital e.g. 100000"
                    value={capital}
                    onChange={(e) => setCapital(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && calculate()}
                  />
                  <button
                    className="mp-btn-primary"
                    onClick={calculate}
                    disabled={calculating}
                  >
                    {calculating ? 'Calculating…' : 'Calculate Allocation'}
                  </button>
                </div>

                <div className="mp-alloc-section">
                  <div className="mp-table-wrap">
                    <table className="mp-table">
                      <thead>
                        <tr>
                          <th>Stock</th>
                          <th style={{ textAlign: 'center' }}>Call</th>
                          <th>Record Price</th>
                          <th>Price</th>
                          <th>P&amp;L</th>
                          <th>Max Buy</th>
                          <th>Weight %</th>
                          <th>Qty</th>
                          <th>Alloc (₹)</th>
                          <th style={{ textAlign: 'center' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(result ? result.stocks : selected.stocks).map((s, i) => (
                          <tr key={s.symbol + i}>
                            <td className="sym" data-label="Stock">{s.symbol}</td>
                            <td data-label="Call" style={{ textAlign: 'center' }}>
                              <HoldingChip status={s.status} />
                            </td>
                            <td data-label="Record Price">{fmtMoney(toNum(s.recordPrice))}</td>
                            <td data-label="Price">{result ? fmtMoney(s.currentPrice) : '—'}</td>
                            <td data-label="P&L" className={result ? pnlClass(s.pnlPercent) : ''}>
                              {result && s.pnlPercent !== null && s.pnlPercent !== undefined ? (
                                <>
                                  {fmtSignedPct(s.pnlPercent)}
                                  <span className="mp-pnl-cell-sub">
                                    {fmtSignedMoney(s.pnlPerShare)}
                                  </span>
                                </>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td data-label="Max Buy">{fmtMoney(toNum(s.maxBuyPrice))}</td>
                            <td data-label="Weight %">{fmt(s.weightPercent)}</td>
                            <td data-label="Qty">{result ? fmt(s.qty) : '—'}</td>
                            <td data-label="Alloc (₹)">{result ? fmtMoney(s.allocated) : '—'}</td>
                            <td data-label="Action" style={{ textAlign: 'center' }}>
                              {result ? (
                                <StatusChip action={s.action} maxBuyPrice={s.maxBuyPrice} />
                              ) : (
                                <span className="mp-chip mp-chip-na">Run sizing</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* ── Rebalance history / activity timeline ───── */}
                <div className="mp-timeline-section">
                  <div className="mp-timeline-head">
                    <span>Rebalance History</span>
                    <label className="mp-timeline-toggle">
                      <input
                        type="checkbox"
                        checked={showPriceOnly}
                        onChange={togglePriceOnly}
                      />
                      Include price-only updates
                    </label>
                  </div>
                  {eventsLoading ? (
                    <div className="mp-timeline-empty">Loading history…</div>
                  ) : events.length === 0 ? (
                    <div className="mp-timeline-empty">
                      No rebalance activity yet for this portfolio.
                    </div>
                  ) : (
                    <div className="mp-timeline">
                      {events.map((b) => (
                        <RebalanceBatch key={b.batchId} batch={b} />
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </section>
      </div>

      {/* ── Info / legend modal ──────────────────────────────── */}
      {showInfo && (
        <div className="mp-modal-overlay" onClick={() => setShowInfo(false)}>
          <div
            className="mp-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Column definitions"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mp-modal-head">
              <h3>How to read this table</h3>
              <button
                type="button"
                className="mp-modal-close"
                aria-label="Close"
                onClick={() => setShowInfo(false)}
              >
                ×
              </button>
            </div>
            <div className="mp-modal-body">
              <div className="mp-modal-row">
                <div className="mp-modal-chips">
                  <HoldingChip status="HOLD" />
                  <HoldingChip status="NEW" />
                  <HoldingChip status="EXIT" />
                </div>
                <p>
                  <b>Call</b> — the admin&rsquo;s current call on each stock:{' '}
                  <b>HOLD</b> (keep), <b>NEW</b> (freshly added), <b>EXIT</b> (book out).
                </p>
              </div>
              <div className="mp-modal-row">
                <div className="mp-modal-chips">
                  <span className="mp-chip mp-chip-buy">Within Buy Range</span>
                </div>
                <p>
                  <b>BUY_ALLOWED</b> — new investors may consider buying; existing
                  investors may add as per allocation.
                </p>
              </div>
              <div className="mp-modal-row">
                <div className="mp-modal-chips">
                  <span className="mp-chip mp-chip-no">No New Entry</span>
                </div>
                <p>
                  <b>NO_NEW_ENTRY</b> — avoid fresh entries above Max Buy Price; existing
                  holders from lower levels may treat as &ldquo;hold, don&rsquo;t add&rdquo;
                  unless research changes.
                </p>
              </div>
              <div className="mp-modal-row">
                <p>
                  <b>Record Price</b> is the admin&rsquo;s reference price.{' '}
                  <b>Price</b> is the current market price. <b>P&amp;L</b> is measured from
                  Record Price → current Price. The decision boundary for new money is{' '}
                  <b>Max Buy Price</b>.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default ModelPortfolio;
